import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { auth } from '../../firebase';
import { signInWithEmailAndPassword } from "firebase/auth";
import style from './LoginPage.module.css';
import { useAuth } from "./AuthContext";

import logoAr from '../../assets/images/logoAr.png';
import logoEn from '../../assets/images/logoEn.png';

function LoginPage() {
    const { isLoggedIn, loading: authLoading } = useAuth();
    const [loginData, setLoginData] = useState({email: '', password: ''});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Email validation function
    const isValidEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    // Trim and validate email
    const validateEmail = (email) => {
        const trimmedEmail = email.trim();
        if(trimmedEmail === ''){
            return { valid: false, message: "Please enter your email" };
        }
        if(!isValidEmail(trimmedEmail)){
            return { valid: false, message: "Please enter a valid email address" };
        }
        return { valid: true, email: trimmedEmail };
    };

    // Validate password
    const validatePassword = (password) => {
        if(password === ''){
            return { valid: false, message: "Please enter your password" };
        }
        if(password.length < 6){
            return { valid: false, message: "Password must be at least 6 characters long" };
        }
        return { valid: true };
    };

    const onSubmit = () => {
        setError(''); // Clear previous errors
        setSuccess(''); // Clear previous success messages

        // Validate email
        const emailValidation = validateEmail(loginData.email);
        if(!emailValidation.valid){
            return setError(emailValidation.message);
        }

        // Validate password
        const passwordValidation = validatePassword(loginData.password);
        if(!passwordValidation.valid){
            return setError(passwordValidation.message);
        }

        // Set loading state
        setLoading(true);

        signInWithEmailAndPassword(auth, emailValidation.email, loginData.password)
            .then((userCredential) => {
                setError(''); // Clear error on success
                setSuccess('Login successful! Redirecting...');
                setLoading(false);
                // TODO: Handle successful login (e.g., redirect)
                console.log('Login successful:', userCredential.user);
            }).catch((error) => {
                setLoading(false);
                // Handle Firebase authentication errors
                let errorMessage = "Login failed. Please check your credentials.";
                if(error.code === 'auth/invalid-email'){
                    errorMessage = "Invalid email address";
                } else if(error.code === 'auth/user-not-found'){
                    errorMessage = "No account found with this email";
                } else if(error.code === 'auth/wrong-password'){
                    errorMessage = "Incorrect password";
                } else if(error.code === 'auth/invalid-credential'){
                    errorMessage = "Invalid email or password";
                } else if(error.code === 'auth/too-many-requests'){
                    errorMessage = "Too many failed attempts. Please try again later.";
                } else if(error.code === 'auth/network-request-failed'){
                    errorMessage = "Network error. Please check your connection.";
                }
                setError(errorMessage);
            });
    }

    // Redirect if already logged in
    if (authLoading) {
        return (
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100vh',
                color: 'white',
                fontFamily: 'Lobster, sans-serif',
                fontSize: '1.5rem'
            }}>
                <div>Loading...</div>
            </div>
        );
    }

    if (isLoggedIn) {
        return <Navigate to="/" replace />;
    }

    return (
        <div className={style.page}>
            <div className={style.imgbg}/>
            <div className={style.bg}/>
            <div className={style.container}>

                <div className={style.logos}>
                    <img className={style.logoEn} src={logoEn} alt="Logo English"/>
                    <img className={style.logoAr} src={logoAr} alt="Logo Arabic"/>
                </div>
                <div className={style.formGroup}>
                    <p className={style.title}>Login</p>

                    <div className={style.input}>
                        <input type="email" className={style.inputField} value={loginData.email} onChange={(e) => setLoginData({...loginData, email: e.target.value})} placeholder="email" name="email" id="email" required/>
                        <label htmlFor="email" className={style.inputLabel}>Email</label>
                    </div>

                    <div className={style.input}>
                        <input type="password" className={style.inputField} value={loginData.password} onChange={(e) => setLoginData({...loginData, password: e.target.value})} placeholder="password" name="password" id="password" required/>
                        <label htmlFor="password" className={style.inputLabel}>Password</label>
                    </div>

                    <button onClick={onSubmit} disabled={loading}>
                        {loading ? 'Logging in...' : 'Login'}
                    </button>

                    {error && <p className={style.error}>{error}</p>}
                    {success && <p className={style.success}>{success}</p>}
                </div>
            </div>
        </div>
    );
}

export default LoginPage;
